import React from 'react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';

import { format } from '../../utils/date';
import { gettext } from '../../utils/text';
import { ShowShape } from './types';

const CURRENCY = '\u00A0€';

const displayPrice = price => {
    if (price % 1 === 0) {
        // has no numbers after floating point
        return price.toFixed(0);
    }
    return price.toFixed(2);
};

const numberSorter = (a, b) => {
    if (a > b) {
        return 1;
    }
    if (b > a) {
        return -1;
    }
    return 0;
};

const formatSpokenDate = showDateTime => {
    const { date, hours } = showDateTime;
    const [hour, minute = 0] = hours.split(':').map(Number);
    const dateTime = new Date(date);
    dateTime.setHours(hour, minute, 0, 0);
    return new Intl.DateTimeFormat(DJ_CONST.languageCode, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(dateTime);
};

const ShowDate = ({ showDateTime, dateElementId }) => {
    const { date, hours } = showDateTime;
    const [hour, minute = 0] = hours.split(':').map(Number);
    const dateTime = new Date(date);
    dateTime.setHours(hour, minute, 0, 0);
    const spokenDate = formatSpokenDate(showDateTime);
    const dateTimeAttr = format(dateTime, "yyyy-MM-dd'T'HH:mm");
    return (
        <>
            <div className="__date" aria-hidden>
                <span className="__weekday">{format(date, 'EEEEEE')}</span>
                <span className="__day">{format(date, 'dd')}</span>
                <span className="__x">
                    {format(date, 'MMM')}{' '}
                    <span className="__year">{date.getFullYear()}</span>
                    <br />
                    {format(date, 'HH:mm')}
                </span>
            </div>
            <time
                className="visually-hidden"
                id={dateElementId}
                dateTime={dateTimeAttr}
                aria-hidden
            >
                {spokenDate}.
            </time>
        </>
    );
};

ShowDate.propTypes = {
    showDateTime: PropTypes.shape({
        date: PropTypes.instanceOf(Date).isRequired,
        hours: PropTypes.string.isRequired,
    }).isRequired,
    dateElementId: PropTypes.string.isRequired,
};

const ShowPrice = ({ price, specialPrices, priceElementId }) => {
    const prices = [];
    let priceText = '';
    let ariaLabel = '';
    if (specialPrices.length) {
        prices.push(...specialPrices.map(sp => parseFloat(sp.price)));
        prices.sort(numberSorter);
        priceText = `${prices
            .map(p => displayPrice(p))
            .join(' / ')}${CURRENCY}`;
        ariaLabel = `${gettext('Price')}: ${priceText}`;
    } else if (price) {
        const { green_price, yellow_price, red_price, blue_price } = price; // eslint-disable-line camelcase
        prices.push(parseFloat(green_price));
        prices.push(parseFloat(yellow_price));
        prices.push(parseFloat(red_price));
        prices.push(parseFloat(blue_price));
        prices.sort(numberSorter);
        priceText = `${displayPrice(prices[0])}${CURRENCY} - ${displayPrice(
            prices[prices.length - 1],
        )}${CURRENCY}`;
        ariaLabel = `${gettext('Price')} ${priceText}.`;
    }
    return (
        <span className="__price" id={priceElementId} aria-label={ariaLabel}>
            {priceText}
        </span>
    );
};

ShowPrice.propTypes = {
    price: PropTypes.shape({
        green_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        yellow_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        red_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        blue_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    specialPrices: PropTypes.arrayOf(
        PropTypes.shape({
            price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
        }),
    ).isRequired,
    priceElementId: PropTypes.string.isRequired,
};

ShowPrice.defaultProps = {
    price: null,
};

const SingleShow = ({ show }) => {
    let remarkBlock = null;
    let showStatusStr = null;
    let showStatus = null;

    if (show.cancelled) {
        showStatusStr = show.cancelled;
        showStatus = gettext('Cancelled');
    } else if (show.sold_out) {
        showStatusStr = show.sold_out;
        showStatus = gettext('Sold out');
    }

    // Accessible IDs for labeling/description
    const titleId = `show-${show.id}-title`;
    const dateId = `show-${show.id}-datetime`;
    const priceId = `show-${show.id}-price`;
    const statusId = `show-${show.id}-status`;
    const categoryId = `show-${show.id}-category`;
    const remarksId = `show-${show.id}-remarks`;

    if (show.staging.guest_staging) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__guest-staging', DJ_CONST.languageCode)}
                aria-hidden
            >
                {gettext('Guest show')}
            </div>
        );
    } else if (show.premiere) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__premiere', DJ_CONST.languageCode)}
                aria-hidden
            >
                {gettext('Premiere')}
            </div>
        );
    } else if (show.excursion) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__remark', DJ_CONST.languageCode)}
                aria-hidden
            >
                + {gettext('Excursion')}
            </div>
        );
    }

    const describedByIds = [categoryId, priceId, remarkBlock ? remarksId : null]
        .filter(Boolean)
        .join(' ');

    const categories = show.staging.staging_note
        ? [show.staging.staging_note]
        : show.staging.types_of_staging;

    const spokenDate = formatSpokenDate(show.time);

    const statusParts = [];
    if (show.cancelled) {
        statusParts.push(gettext('Cancelled'));
    } else if (show.sold_out) {
        statusParts.push(gettext('Sold out'));
    }
    if (show.last_tickets) {
        statusParts.push(gettext('Last tickets'));
    }
    if (show.location) {
        statusParts.push(show.location.name);
    }

    const statusText = statusParts.length
        ? ` ${gettext('Status')}: ${statusParts.join(', ')}.`
        : '';

    return (
        <li
            className={clsx('__show position-relative', {
                '__sold-out': showStatusStr,
            })}
        >
            <span className="visually-hidden" id={categoryId}>
                {gettext('Categories')}: {categories.filter(Boolean).join(', ')}
                ,
            </span>
            <a
                href={show.url}
                aria-label={`${show.staging.name}. ${spokenDate}.${statusText}`}
                aria-describedby={describedByIds}
            >
                <div
                    className="__preview"
                    style={{ backgroundImage: `url(${show.staging.image})` }}
                    aria-hidden
                />
                <div className="__details">
                    <ShowDate showDateTime={show.time} dateElementId={dateId} />
                    <h3 className="__title" id={titleId}>
                        {show.staging.name}
                    </h3>
                    <div className="__meta">
                        <span className="__category" aria-hidden>
                            {show.staging.staging_note ? (
                                <span aria-hidden>
                                    {show.staging.staging_note}
                                </span>
                            ) : (
                                show.staging.types_of_staging.map(type => (
                                    <span aria-hidden key={type}>
                                        {type}
                                    </span>
                                ))
                            )}
                        </span>
                        <ShowPrice
                            price={show.price}
                            specialPrices={show.special_prices}
                            priceElementId={priceId}
                        />
                    </div>
                    <div className="__meta">
                        <span
                            className="__places"
                            id={showStatus ? statusId : undefined}
                            aria-label={
                                statusParts.length
                                    ? `${statusParts.join(', ')}.`
                                    : ''
                            }
                            aria-hidden={!statusParts.length}
                        >
                            <span aria-hidden>{showStatus}</span>
                            {show.last_tickets ? (
                                <span aria-hidden>
                                    {gettext('Last tickets')}
                                </span>
                            ) : null}
                            {show.location ? (
                                <span aria-hidden>{show.location.name}</span>
                            ) : null}
                        </span>
                    </div>
                </div>
                {remarkBlock}
            </a>
        </li>
    );
};

SingleShow.propTypes = {
    show: ShowShape.isRequired,
};

export default SingleShow;
